import React, { useState, useCallback, useRef, useEffect } from 'react';

import {
	Box,
	TextField,
	Divider,
	Typography,
	Switch,
	Button,
	IconButton,
} from '@mui/material';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
	IQuestionData,
	IQuestionnaireBlockProps,
	IQuestionnaireTemplateStateData,
	ISectionTemplateStateData,
} from 'src/features/questionnaire_template/domain/types';
import {
	generateNextHiddenSectionName,
	generateUniqueId,
	isFrontendId,
} from 'src/features/questionnaire_template/service/questionnaireTemplateService';

import { SectionBlock } from './SectionBlock';

export const QuestionnaireBlock: React.FC<IQuestionnaireBlockProps> = ({
	mode,
	defaultData,
	onChange,
	validation,
	onDelete,
}) => {
	const [questionnaireData, setQuestionnaireData] =
		useState<IQuestionnaireTemplateStateData>(defaultData);
	const [sections, setSections] = useState<(string | number)[]>(
		Object.keys(defaultData?.sections || {}),
	);

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const debounceKeys: (keyof IQuestionnaireTemplateStateData)[] = [
		'name',
		'published',
		'index',
	];
	const debounceDeps = debounceKeys.map((key) => questionnaireData[key]);
	const enableOnChangeUseEffectRef_1 = useRef<boolean>(false);
	const enableOnChangeUseEffectRef_2 = useRef<boolean>(false);
	const enableDefaultDataChangeUseEffectRef = useRef<boolean>(false);

	const handleAddSection = (type?: 'hidden' | 'normal') => {
		const newSectionId = generateUniqueId('s');

		if (type === 'hidden') {
			const lastSectionId = Object.keys(
				questionnaireData.sections || {},
			).at(-1);
			const lastSection = lastSectionId
				? questionnaireData.sections[lastSectionId]
				: undefined;

			if (
				lastSectionId &&
				lastSection?.is_hidden &&
				!lastSection.is_deleted
			) {
				const newQuestionId = generateUniqueId('q');
				const updatedQuestions = {
					...lastSection.questions,
					[newQuestionId]: {
						text: '',
						type: '',
						serial_number: '',
						is_required: false,
						options: [],
					} as IQuestionData,
				};

				handleSectionChange(lastSectionId, {
					...lastSection,
					questions: updatedQuestions,
				});
			} else {
				const questionId = generateUniqueId('q');
				setSections((prev) => [...prev, newSectionId]);

				handleSectionChange(newSectionId, {
					name: generateNextHiddenSectionName(
						questionnaireData.sections,
					),
					display_name: '',
					is_hidden: true,
					questions: {
						[questionId]: {
							text: '',
							type: '',
							serial_number: '',
							is_required: false,
							options: [],
						},
					},
				});
			}
		} else {
			setSections((prev) => [...prev, newSectionId]);
		}
	};

	const handleSectionChange = useCallback(
		(
			sectionId: number | string,
			sectionData: ISectionTemplateStateData,
		) => {
			setQuestionnaireData((prev) => {
				const currentSection = prev.sections[sectionId];
				const isEqual =
					JSON.stringify(currentSection) ===
					JSON.stringify(sectionData);

				if (isEqual) return prev;

				return {
					...prev,
					sections: { ...prev.sections, [sectionId]: sectionData },
				};
			});
		},
		[],
	);

	const handleDeleteSection = useCallback((sectionId: number | string) => {
		setSections((prev) => prev.filter((sid) => sid !== sectionId));
		setQuestionnaireData((prev) => {
			const updatedSections = { ...prev.sections };

			if (isFrontendId(sectionId)) {
				delete updatedSections[sectionId];
			} else {
				if (updatedSections[sectionId]) {
					updatedSections[sectionId] = {
						...updatedSections[sectionId],
						is_deleted: true,
					};
				}
			}
			return { ...prev, sections: updatedSections };
		});
	}, []);

	useDeepCompareEffect(() => {
		if (!enableOnChangeUseEffectRef_1.current) {
			enableOnChangeUseEffectRef_1.current = true;
			return;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			onChange(questionnaireData);
		}, 300);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [...debounceDeps, onChange]);

	useDeepCompareEffect(() => {
		if (!enableOnChangeUseEffectRef_2.current) {
			enableOnChangeUseEffectRef_2.current = true;
			return;
		}

		onChange(questionnaireData);
	}, [questionnaireData.sections, onChange]);

	useEffect(() => {
		// TO DO: After integration remove defaultData related useEffect if redundant
		if (!enableDefaultDataChangeUseEffectRef.current) {
			enableDefaultDataChangeUseEffectRef.current = true;
			return;
		}

		if (defaultData) {
			setQuestionnaireData(defaultData);
			setSections(Object.keys(defaultData.sections || {}));
		}
	}, [defaultData]);

	return (
		<Box
			sx={{
				backgroundColor: 'white',
				p: 3,
				borderRadius: '6px',
				border: '2px dashed #71717A',
			}}
		>
			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
				<TextField
					label='Questionnaire Title'
					variant='outlined'
					fullWidth
					value={questionnaireData.name}
					onChange={(e) =>
						setQuestionnaireData({
							...questionnaireData,
							name: e.target.value,
							is_updated: true,
						})
					}
					sx={{
						flex: 2,
						'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
							{
								borderColor: '#BE3327',
							},
						'& .MuiInputLabel-root.Mui-focused': {
							color: '#BE3327',
						},
					}}
					error={!!validation?.name}
					helperText={validation?.name}
				/>

				<TextField
					type='number'
					label='Index'
					variant='outlined'
					fullWidth
					value={questionnaireData.index}
					onChange={(e) =>
						setQuestionnaireData({
							...questionnaireData,
							index: e.target.value,
							is_updated: true,
						})
					}
					sx={{
						flex: 1,
						'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
							{
								borderColor: '#BE3327',
							},
						'& .MuiInputLabel-root.Mui-focused': {
							color: '#BE3327',
						},
					}}
					error={!!validation?.index}
					helperText={validation?.index}
				/>
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					mt: 2,
					gap: 1,
				}}
			>
				{mode === 'create' && (
					<IconButton onClick={() => onDelete?.()}>
						<img
							src='/delete.svg'
							alt='delete'
							width={16}
							height={18}
						/>
					</IconButton>
				)}

				<Divider orientation='vertical' flexItem />

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Typography fontSize='16px' lineHeight='24px'>
						Published
					</Typography>
					<Switch
						checked={questionnaireData.published}
						onChange={(e) =>
							setQuestionnaireData({
								...questionnaireData,
								published: e.target.checked,
								is_updated: true,
							})
						}
						sx={{
							'& .MuiSwitch-switchBase.Mui-checked': {
								color: '#E53326',
							},
							'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
								{
									backgroundColor: '#E53326',
								},
						}}
					/>
				</Box>
			</Box>

			{/* Section Blocks */}
			{sections.map((sectionId) => (
				<SectionBlock
					key={sectionId}
					id={sectionId}
					onChange={handleSectionChange}
					onDelete={handleDeleteSection}
					defaultData={questionnaireData.sections[sectionId]}
					validation={validation?.sections?.[sectionId]}
				/>
			))}

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					mt: 2,
				}}
			>
				<Button
					variant='contained'
					sx={{ backgroundColor: '#00000099', mr: 1 }}
					onClick={() => handleAddSection('hidden')}
				>
					Add Question
				</Button>

				<Button
					variant='contained'
					sx={{ backgroundColor: '#00000099' }}
					onClick={() => handleAddSection('normal')}
				>
					Add Section
				</Button>
			</Box>
		</Box>
	);
};
